import { lazy, type ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Bell,
  Building2,
  Mail,
  LifeBuoy,
  Users,
  UserCircle,
} from "lucide-react";
import { SETTINGS_DESTINATIONS } from "@shared/settingsDestinations";
import type { SettingsArea } from "@/lib/settingsTouched";

// Providers and the app shell use this catalog even when Settings is closed.
// Keep panel implementations out of that startup dependency graph.
const DashboardCustomizeContent = lazy(() => import("./DashboardCustomizeContent").then(m => ({ default: m.DashboardCustomizeContent })));
const DataImportExportTab = lazy(() => import("./DataSettingsContent").then(m => ({ default: m.DataImportExportTab })));
const AuthorizedUsersContent = lazy(() => import("./AuthorizedUsersContent").then(m => ({ default: m.AuthorizedUsersContent })));
const AccountInfoContent = lazy(() => import("./AccountInfoContent").then(m => ({ default: m.AccountInfoContent })));
const BillingSettingsContent = lazy(() => import("./BillingSettingsContent").then(m => ({ default: m.BillingSettingsContent })));
const HelpSettingsContent = lazy(() => import("./HelpSettingsContent").then(m => ({ default: m.HelpSettingsContent })));
const BusinessBasicsTab = lazy(() => import("@/components/settings/BusinessBasicsPanel").then(m => ({ default: m.BusinessBasicsTab })));
const EmailSignatureTab = lazy(() => import("./EmailSignatureContent").then(m => ({ default: m.EmailSignatureTab })));
const MergeTagsSettingsContent = lazy(() => import("./MergeTagsSettingsContent").then(m => ({ default: m.MergeTagsSettingsContent })));
const EmailConnectionTab = lazy(() => import("./EmailSettingsContent").then(m => ({ default: m.EmailConnectionTab })));
const CalendarAndTaskSettingsContent = lazy(() => import("./TasksSettingsContent").then(m => ({ default: m.CalendarAndTaskSettingsContent })));

export type SettingsGroupId = "business" | "account";

// A tab within a section. Its body renders "flat" (no outer bordered card — the
// hub content pane already pads), mirroring the ContactCard tab bodies.
export type SettingsTab = {
  id: string;
  label: string;
  Component: ComponentType;
  // When set, the tab only appears if the matching runtime capability is
  // configured (resolved in the hub, which has the access-tier context).
  gate?: "billing" | "owner";
  // When true, the tab stays visible but is grayed out (not clickable) until
  // the user has an approved texting number (resolved in the hub).
  requiresSmsReady?: boolean;
};

export type SettingsSection = {
  id: string;
  label: string;
  group: SettingsGroupId;
  icon: LucideIcon;
  // A section either renders a single body (Component) or a set of tabs. Exactly
  // one of these is set.
  Component?: ComponentType;
  tabs?: SettingsTab[];
  // Workspace sections stamp a durable "the user configured this" flag while
  // open + saving (see useMarkSettingsTouched). Only areas in SETTINGS_AREAS
  // qualify.
  touchArea?: SettingsArea;
  // When set, the section only appears if the matching runtime capability is
  // configured (resolved in the hub, which has the access-tier context).
  // "owner" hides the section from authorized members working in an owner's
  // workspace (Task 1945).
  gate?: "billing" | "owner";
};

export const SETTINGS_GROUPS: { id: SettingsGroupId; label: string }[] = [
  { id: "business", label: "Business" },
  { id: "account", label: "Account" },
];

export const SETTINGS_SECTIONS: SettingsSection[] = [
  // Business
  {
    id: SETTINGS_DESTINATIONS.businessInfo.sectionId,
    label: SETTINGS_DESTINATIONS.businessInfo.label,
    group: "business",
    icon: Building2,
    tabs: [
      { id: "basics", label: "Basics", Component: BusinessBasicsTab },
      { id: "calendar", label: SETTINGS_DESTINATIONS.calendar.label, Component: CalendarAndTaskSettingsContent },
      { id: "dashboard", label: "Dashboard", Component: DashboardCustomizeContent },
      // Email Signature moved here from Email & Texts (Connectors) so it sits
      // alongside other business-identity settings (name, logo, colors).
      // Legacy callers that open messaging/signature are redirected here by
      // SettingsHubProvider.
      { id: "signature", label: "Email Signature", Component: EmailSignatureTab },
      { id: "merge-tags", label: "Merge Tags", Component: MergeTagsSettingsContent },
      { id: "data", label: SETTINGS_DESTINATIONS.importExport.label, Component: DataImportExportTab },
    ],
  },
  {
    id: SETTINGS_DESTINATIONS.domainEmail.sectionId,
    label: SETTINGS_DESTINATIONS.domainEmail.label,
    group: "business",
    icon: Mail,
    Component: EmailConnectionTab,
  },
  // Account
  // Account Info (Task 2218): lets the owner correct their first/last name and
  // email after onboarding without reopening the wizard. Billing, Authorized
  // Users, and Notifications are now tabs here so all account-level settings
  // live in one place. Gate is "owner" at the section level; Billing tab has
  // an additional "billing" gate; Authorized Users tab has an "owner" gate.
  {
    id: "account-info",
    label: "Account Info",
    group: "account",
    icon: UserCircle,
    gate: "owner",
    tabs: [
      { id: "profile", label: "Account Info", Component: AccountInfoContent },
      // Billing: Stripe-configured and owner-only.
      { id: "billing", label: SETTINGS_DESTINATIONS.billing.label, Component: BillingSettingsContent, gate: "billing" },
      // Authorized Users: owner-only (already gated at section level, but
      // the tab gate ensures the tab is hidden for authorized-user sessions
      // that might open this section in future if the section gate is relaxed).
      { id: "authorized-users", label: "Authorized Users", Component: AuthorizedUsersContent, gate: "owner" },
    ],
  },
  {
    id: SETTINGS_DESTINATIONS.help.sectionId,
    label: SETTINGS_DESTINATIONS.help.label,
    group: "account",
    icon: LifeBuoy,
    Component: HelpSettingsContent,
  },
];

export const DEFAULT_SECTION_ID = "business-info";

export function findSection(id: string): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((s) => s.id === id);
}
